import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, Info, Loader } from 'lucide-react';
import { useOnClickOutside } from '../hooks/useOnClickOutside';

interface ConfirmationModalProps {
  isOpen?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  /** Extra content under the message: a list of what will happen, or an option. */
  children?: ReactNode;
  /**
   * `danger` (default) for destructive actions. `accent` for a confirmation that
   * changes something without destroying it, such as applying a previewed plan.
   */
  tone?: 'danger' | 'accent';
  /** Keep the confirm button disabled (e.g. while a preview is still loading). */
  confirmDisabled?: boolean;
}

export const ConfirmationModal = ({
  isOpen = true,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  children,
  tone = 'danger',
  confirmDisabled = false,
}: ConfirmationModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div ref={modalRef} className={children ? 'modal-panel max-w-lg max-md:max-w-[95vw]' : 'modal-panel max-w-md'} role="alertdialog" aria-modal="true" aria-labelledby="confirmation-title">
        <button type="button" onClick={onClose} className="modal-close" title="Close" aria-label="Close">
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-4 pr-6">
          <span className={tone === 'accent' ? 'icon-tile icon-tile-accent' : 'icon-tile icon-tile-danger'}>
            {tone === 'accent' ? <Info className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="confirmation-title" className="modal-title">{title}</h2>
            <p className="text-sm text-glass-400 mt-1.5">{message}</p>
            {children && <div className="mt-3 space-y-3">{children}</div>}
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="glass-button-secondary" disabled={isLoading}>
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={tone === 'accent' ? 'btn-primary min-w-[7rem]' : 'glass-button bg-red-500/85 hover:bg-red-500 border-red-400/40 text-on-solid min-w-[7rem]'}
            disabled={isLoading || confirmDisabled}
          >
            {isLoading ? <Loader className="w-4 h-4 animate-spin" /> : confirmText}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
